// module.engine-vitals (contract §4.8.1): EngineVitalsModule
// (UnderwayModules.tsx): for each engine heard now (up to four, named when
// there are several), its readings as medium readouts, each left out when the
// boat does not report it: RPM, coolant (coloured by its limit), oil pressure
// (red when lost), load, fuel rate, exhaust, alternator and hours; then the
// owner-named probes with their fault or "no signal" line; or, with no engine
// heard, the line saying so and the hours of the engines at rest. Dense
// readouts are smaller.
//
// A readout's value comes first in the markup, its label shown above it by
// `order`, so a reading the boat lacks (an empty value) hides its whole cell;
// a cell takes its label's width, so a hidden one takes none. The space
// between lines is each shown line's own, so an engine slot the boat does not
// fill takes no room.

import { css, h } from "./kit.js";
import { F, R } from "./module-kit.js";

const ENGINES = 4;
const PROBES = 8;
const REST = 4;

const cell = (label, value, unit, k, extra = "") =>
  h(
    "div",
    { class: `ev-c${extra}` },
    h("span", { class: "rd-v", ...R(value, k) }),
    typeof unit === "string"
      ? h("span", { class: "rd-u" }, unit)
      : h("span", { class: "rd-u", ...R(unit.field, k) }),
    h("span", { class: "ev-l" }, label),
  );

export function moduleEngineVitals() {
  const engines = [];
  for (let k = 0; k < ENGINES; k += 1)
    engines.push(
      h(
        "div",
        { class: "ev-e" },
        h("p", { class: "ev-n", ...R("engine-name", k) }),
        h(
          "div",
          { class: "ev-g" },
          cell("RPM", "engine-rpm", "rpm", k),
          cell(
            "Coolant",
            "engine-coolant",
            { field: "engine-coolant-unit" },
            k,
          ),
          cell("Oil", "engine-oil", { field: "engine-oil-unit" }, k),
          cell("Load", "engine-load", "%", k),
          cell(
            "Fuel rate",
            "engine-fuel-rate",
            { field: "engine-fuel-rate-unit" },
            k,
          ),
          cell(
            "Exhaust",
            "engine-exhaust",
            { field: "engine-exhaust-unit" },
            k,
          ),
          cell("Alternator", "engine-alternator", "V", k),
          cell("Hours", "engine-hours", "h", k),
        ),
      ),
    );
  const probes = [];
  for (let k = 0; k < PROBES; k += 1)
    probes.push(
      h(
        "div",
        { class: "ev-c ev-p" },
        h("i", { class: "ev-ps", ...R("probe-state", k) }),
        h("span", { class: "rd-v", ...R("probe-value", k) }),
        h("span", { class: "rd-u", ...R("probe-unit", k) }),
        h("span", { class: "ev-l", ...R("probe-label", k) }),
        h("span", { class: "ev-sub", ...R("probe-note", k) }),
      ),
    );
  const rest = [];
  for (let k = 0; k < REST; k += 1)
    rest.push(
      h(
        "div",
        { class: "ev-c" },
        h("span", { class: "rd-v", ...R("rest-hours", k) }),
        h("span", { class: "rd-u" }, "h"),
        h("span", { class: "ev-l", ...R("rest-label", k) }),
      ),
    );
  return {
    html: [
      h("i", { class: "mo-flag", ...F("dense") }),
      h("p", { class: "mo-msg ev-none", ...F("none") }),
      h(
        "div",
        { class: "ev" },
        engines,
        h("div", { class: "ev-g" }, probes, rest),
      ),
    ],
    css: css(`
      .ev { display: flex; flex-direction: column; margin-top: -8px; }
      .ev-e { display: flex; flex-direction: column; }
      .ev .ev-n { font-size: 13px; font-weight: 600; color: var(--muted); margin: 8px 0 -4px; }
      .ev-n:empty { display: none; }
      .ev-g { display: flex; flex-wrap: wrap; }
      .ev-c { flex: 0 1 auto; display: flex; flex-wrap: wrap; align-items: baseline; column-gap: 0.18em; font-size: 34px; min-width: 0;
        margin-top: 8px; }
      .mo-flag[data-state=on] ~ .ev .ev-c { font-size: 24px; }
      .ev-c .ev-l { order: -1; flex-basis: 100%; min-width: 128px; padding-right: 8px; font-size: 13px; font-weight: 500; color: var(--muted); white-space: nowrap; margin-bottom: 2px; }
      .ev-c .rd-v:empty, .ev-c .rd-v:empty ~ * { display: none; }
      .ev-c .rd-v[data-tone=warn], .ev-c .rd-v[data-zone=warn] { color: var(--attention); }
      .ev-c .rd-v[data-tone=bad], .ev-c .rd-v[data-zone=bad], .ev-c .rd-v[data-state=lost] { color: var(--danger); }
      .ev-c .rd-v[data-quality=stale], .ev-c .rd-v[data-quality=missing] { color: var(--muted); }
      /* At night a stale value keeps the text colour: grey under the night tint reads below the app's own values (§9.6, TOM-665). */
      html[data-theme=night] .ev-c .rd-v[data-quality=stale] { color: var(--text); }
      .ev-ps { display: none; }
      .ev-sub { flex-basis: 100%; font-size: 13px; color: var(--muted); margin-top: 2px; }
      .ev-sub:empty { display: none; }
      .ev-ps[data-state=faulted] ~ .rd-v { color: var(--attention); }
      /* At night a warning value keeps the text colour: --attention under the night tint reads below the app's own values; its glyph keeps the tone (§9.6, TOM-665). */
      html[data-theme=night] .ev-c .rd-v[data-tone=warn], html[data-theme=night] .ev-c .rd-v[data-zone=warn],
        html[data-theme=night] .ev-ps[data-state=faulted] ~ .rd-v { color: var(--text); }
      .ev-p .ev-l:empty { display: none; }
    `),
  };
}
